import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ComparisonFilters } from '@/components/comparison-filters';
import { KpisComparison } from '@/components/kpis-comparison';
import { OfferSelector } from '@/components/offer-selector';
import { Sidebar } from '@/components/ui-v2/sidebar';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Campaign,
  DealItemWithVersions,
  OfferEvolutionInsightsV2,
} from '@/lib/graphql/generated/graphql';
import { useCompareKpis } from '@/lib/hooks/useCompareKpis';
import { useLatestPreviousCampaigns } from '@/lib/hooks/useLatestPreviousCampaigns';
import { usePreviousCampaignsRevenue } from '@/lib/hooks/usePreviousCampaignsRevenue';
import { trackEvent } from '@/lib/mixpanel';
import {
  ColumnFilterId,
  useSetColumnFilter,
} from '@/states/column-filters.state';
import { comparisonFilterState } from '@/states/comparison-filter.state';
import { evolutionInsightsState } from '@/states/evolution-insights.state';
import { kpiComparisonState } from '@/states/kpi-comparison.state';
import { useResetSelection } from '@/states/row-selection.state';
import { ComparisonFilter } from '@/types/compare-offers';
import { KPIComparison } from '@/types/kpi-comparison';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { AlertCircle, BarChart3, Loader2 } from 'lucide-react';

interface CompareOffersSidebarProps {
  dealId: string;
  brandId?: string;
  onClose: () => void;
  products: DealItemWithVersions[];
}

export function CompareOffersSidebar({
  dealId,
  onClose,
  products,
}: CompareOffersSidebarProps) {
  const { t } = useTranslation();
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(
    null
  );
  const [comparisonFilter, setComparisonFilter] = useAtom(
    comparisonFilterState
  );
  const evolutionInsights = useAtomValue(evolutionInsightsState);
  const setKPIComparison = useSetAtom(kpiComparisonState);
  const { resetSelection } = useResetSelection();
  const { setColumnFilter } = useSetColumnFilter();

  // Hooks for fetching data
  const { previousCampaigns, loading: campaignsLoading } =
    useLatestPreviousCampaigns();
  // GMV is fetched when this sidebar opens rather than on deal load; the campaigns themselves are
  // already there, so the selector renders immediately and the amounts fill in.
  const { campaignsWithRevenue } = usePreviousCampaignsRevenue(
    dealId,
    previousCampaigns
  );
  const {
    fetchKpis,
    kpisData,
    loading: isLoading,
    error: hasError,
    reset: resetKpis,
  } = useCompareKpis();

  // The sidebar is mounted only while open, so a mount maps 1:1 to an opening.
  useEffect(() => {
    trackEvent('OFFER_ANALYSIS_SIDEBAR_OPENED', { dealId });
  }, [dealId]);

  // Handle campaign selection and trigger comparison
  const handleCampaignSelect = async (campaign: Campaign) => {
    setSelectedCampaign(campaign);

    trackEvent('OFFER_COMPARISON_SELECTED', {
      dealId,
      compareToSaleId: campaign.saleId,
      campaignName: campaign.name,
    });

    resetKpis();
    setComparisonFilter({
      activeFilter: undefined,
      filteredItemIds: [],
    });
    setColumnFilter(ColumnFilterId.__comparisonFilter, []);

    // Fetch comparison data
    try {
      await fetchKpis({ dealId, compareToSaleId: campaign.saleId });
    } catch (error) {
      console.error('Error fetching comparison data:', error);
    }
  };

  // Handle filter clicks
  const handleFilterClick = (filter: ComparisonFilter) => {
    setKPIComparison(filter.view);

    // Reset row selection when applying comparison filters to prevent conflicts
    resetSelection();

    if (comparisonFilter.activeFilter === filter.type) {
      // Deactivate current filter
      setComparisonFilter({
        activeFilter: undefined,
        filteredItemIds: [],
      });
      setColumnFilter(ColumnFilterId.__comparisonFilter, []);
    } else {
      setComparisonFilter({
        activeFilter: filter.type,
        filteredItemIds: filter.itemIds,
      });
      setColumnFilter(ColumnFilterId.__comparisonFilter, filter.itemIds);
    }
  };

  const handleResetClick = useCallback(() => {
    resetSelection();
    setKPIComparison(KPIComparison.NONE);
    setComparisonFilter({
      activeFilter: undefined,
      filteredItemIds: [],
    });
    setColumnFilter(ColumnFilterId.__comparisonFilter, []);
  }, [resetSelection, setKPIComparison, setComparisonFilter, setColumnFilter]);

  const computeBadgeCount = (comparison: OfferEvolutionInsightsV2) =>
    comparison.discountIncreases.length +
    comparison.discountReductions.length +
    comparison.stockReductions.length +
    comparison.addedProducts.length;

  const handleClose = () => {
    handleResetClick();
    resetKpis();
    onClose();
  };

  return (
    <Sidebar
      title={
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 shrink-0" />
          <h3 className="truncate text-lg font-medium">
            {t('components.compare_offers_sidebar.title')}
          </h3>
        </div>
      }
      onClose={handleClose}
    >
      <div className="space-y-6">
        {/* Campaign Selection */}
        <Card>
          <CardHeader className="pb-2 pt-6">
            <CardTitle className="text-lg">
              {t('components.compare_offers_sidebar.choose_offer_to_compare')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {campaignsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="mr-2 h-6 w-6 animate-spin" />
                <span>
                  {t('components.compare_offers_sidebar.loading_campaigns')}
                </span>
              </div>
            ) : (
              <OfferSelector
                campaigns={campaignsWithRevenue}
                selectedCampaignId={selectedCampaign?.saleId}
                onSelect={handleCampaignSelect}
                loading={isLoading}
              />
            )}
          </CardContent>
        </Card>

        {/* Analytics Results */}
        <Tabs defaultValue="kpis" className="w-full">
          {selectedCampaign && (
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="kpis">
                {t('components.compare_offers_sidebar.kpis')}
              </TabsTrigger>
              <TabsTrigger value="details" className="flex items-center gap-2">
                {t('components.compare_offers_sidebar.details')}
                {evolutionInsights && (
                  <Badge variant="secondary" className="ml-1">
                    {computeBadgeCount(evolutionInsights)}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
          )}

          <TabsContent value="kpis" className="mt-6">
            {isLoading ? (
              <Card>
                <CardContent className="flex items-center justify-center py-8">
                  <Loader2 className="mr-2 h-6 w-6 animate-spin" />
                  <span>
                    {t(
                      'components.compare_offers_sidebar.analysis_in_progress'
                    )}
                  </span>
                </CardContent>
              </Card>
            ) : hasError ? (
              <Card>
                <CardContent className="flex items-center justify-center py-8">
                  <AlertCircle className="mr-2 h-6 w-6 text-red-500" />
                  <span className="text-red-600">
                    {t(
                      'components.compare_offers_sidebar.comparative_analysis_error'
                    )}
                  </span>
                </CardContent>
              </Card>
            ) : products.length > 0 ? (
              <KpisComparison kpis={kpisData} products={products} />
            ) : (
              <Card>
                <CardContent className="p-4">
                  <p className="text-center text-sm text-gray-500">
                    {t(
                      'components.compare_offers_sidebar.kpi_data_unavailable'
                    )}
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {selectedCampaign && (
            <TabsContent value="details" className="mt-6">
              {isLoading ? (
                <Card>
                  <CardContent className="flex items-center justify-center py-8">
                    <Loader2 className="mr-2 h-6 w-6 animate-spin" />
                    <span>
                      {t(
                        'components.compare_offers_sidebar.detailed_analysis_in_progress'
                      )}
                    </span>
                  </CardContent>
                </Card>
              ) : hasError ? (
                <Card>
                  <CardContent className="flex items-center justify-center py-8">
                    <AlertCircle className="mr-2 h-6 w-6 text-red-500" />
                    <span className="text-red-600">
                      {t(
                        'components.compare_offers_sidebar.detailed_analysis_error'
                      )}
                    </span>
                  </CardContent>
                </Card>
              ) : evolutionInsights ? (
                <ComparisonFilters
                  onFilterClick={handleFilterClick}
                  activeFilter={comparisonFilter.activeFilter}
                  evolutionInsights={evolutionInsights}
                  onResetClick={handleResetClick}
                />
              ) : (
                <Card>
                  <CardContent className="p-4">
                    <p className="text-center text-sm text-gray-500">
                      {t(
                        'components.compare_offers_sidebar.comparison_data_unavailable'
                      )}
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          )}
        </Tabs>
      </div>
    </Sidebar>
  );
}
